// ---------------------------------------------------------------------------
// WhatsApp module — shared constants, helpers, and demo seed data.
//
// This is a UI/UX build. All data below is local mock seed data so the module
// is fully demoable before the backend WhatsApp endpoints are wired. Each
// sub-view marks with `// TODO(api):` exactly where a real call will replace
// the mock. Nothing here sends a real message.
// ---------------------------------------------------------------------------

// WhatsApp brand green — used ONLY for the chat surface (outgoing bubbles, the
// "online" accent) so the inbox reads as WhatsApp. Everything else uses the
// admin theme palette so the module stays on-brand with the panel.
export const WA_GREEN = '#25D366';
export const WA_GREEN_DARK = '#128C7E';
export const WA_TEAL = '#075E54';

// Message delivery status → meaning + tick rendering (handled in the bubble).
export const MSG_STATUS = {
  pending: { label: 'Sending', ticks: 0 },
  sent: { label: 'Sent', ticks: 1 },
  delivered: { label: 'Delivered', ticks: 2 },
  read: { label: 'Read', ticks: 2, blue: true },
  failed: { label: 'Failed', ticks: 0 },
};

// Template approval states mirror Meta's review lifecycle.
export const TEMPLATE_STATUS = {
  APPROVED: { label: 'Approved', color: 'success' },
  PENDING: { label: 'In Review', color: 'warning' },
  REJECTED: { label: 'Rejected', color: 'error' },
  PAUSED: { label: 'Paused', color: 'default' },
};

export const TEMPLATE_CATEGORIES = ['MARKETING', 'UTILITY', 'AUTHENTICATION'];

// Template action-button kinds. WhatsApp allows mixing quick-reply buttons with
// call-to-action buttons (one phone, up to two website links) on a template.
export const TEMPLATE_BUTTON_TYPES = [
  { value: 'QUICK_REPLY', label: 'Quick reply' },
  { value: 'PHONE_NUMBER', label: 'Call phone number' },
  { value: 'URL', label: 'Visit website' },
];

// Default number used to seed the "send a test" dialog so the operator can fire
// a template at a known handset before wiring it into a broadcast.
export const WA_TEST_NUMBER = '9909719458';

// Coerce stored buttons (which may be legacy plain strings) into the
// { type, text, value } shape the builder and preview work with.
export const normalizeButtons = (buttons = []) =>
  buttons.map((b) => (typeof b === 'string'
    ? { type: 'QUICK_REPLY', text: b, value: '' }
    : { type: b.type || 'QUICK_REPLY', text: b.text || '', value: b.value || '' }));

export const TEMPLATE_LANGUAGES = [
  { code: 'en_US', label: 'English (US)' },
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'Hindi' },
  { code: 'gu', label: 'Gujarati' },
];

export const BROADCAST_STATUS = {
  draft: { label: 'Draft', color: 'default' },
  scheduled: { label: 'Scheduled', color: 'info' },
  sending: { label: 'Sending', color: 'warning' },
  paused: { label: 'Paused', color: 'default' },
  completed: { label: 'Completed', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
};

// Tag color palette — drawn from the theme accents so chips stay consistent.
export const TAG_COLORS = [
  '#6366F1', '#22C55E', '#F97316', '#1890FF', '#EF4444',
  '#A855F7', '#FFC107', '#00897b', '#EC4899', '#637381',
];

// ---- helpers --------------------------------------------------------------

// Emoji palette for the composer picker.
export const EMOJIS = [
  // Smileys & people
  '😀', '😁', '😂', '🤣', '😊', '😍', '😘', '😎', '🤩', '🤗',
  '🤔', '😅', '🙂', '😉', '🥳', '😇', '😋', '😜', '🤪', '😏',
  '😢', '😭', '😡', '😴', '🤤', '🤒', '🥰', '🤓', '🫶', '🙈',
  // Gestures
  '🙏', '👍', '👎', '👏', '🙌', '💪', '🤝', '👌', '✌️', '🤞',
  '👋', '🤙', '👇', '👉', '👈', '☝️', '🫡', '🤷', '🙆', '🙅',
  // Symbols & reactions
  '🔥', '✨', '🎉', '🎊', '❤️', '🧡', '💛', '💚', '💙', '💜',
  '💯', '⭐', '🌟', '💥', '💫', '❗', '❓', '💬', '👀', '🥺',
  // Status / commerce (commonly used for orders & support)
  '✅', '❌', '⚠️', '⏳', '🕐', '📦', '🚚', '💰', '💳', '🛒',
  '🛍️', '🏷️', '🎁', '📋', '📄', '📷', '📸', '🔗', '📞', '📍',
];

// Human-readable byte size.
export const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// First http(s) URL in a string, or null.
export const firstUrl = (text = '') => {
  const m = String(text).match(/https?:\/\/[^\s]+/i);
  return m ? m[0] : null;
};

// Whether a URL looks like a direct image link, so a typed link can be shown as
// an inline image preview instead of a generic link card. Tolerates a trailing
// query string (e.g. CDN/signed URLs: .../photo.jpg?token=…).
export const isImageUrl = (url = '') =>
  /\.(png|jpe?g|gif|webp|bmp|svg|avif)(\?[^\s]*)?$/i.test(String(url));

const titleCase = (s) => s.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).trim();

// Build a link-preview card from a URL. This is a CLIENT-SIDE MOCK of the
// OpenGraph metadata a real preview needs.
// TODO(api): real previews come from the backend fetching the page's OG tags
// (and WhatsApp generates its own when you send text with preview_url: true).
export const buildLinkPreview = (url) => {
  let u;
  try { u = new URL(url); } catch (e) { return null; }
  const domain = u.hostname.replace(/^www\./, '');
  const brand = titleCase(domain.split('.')[0]);
  const lastSeg = u.pathname.split('/').filter(Boolean).pop() || '';
  return {
    url,
    domain,
    title: lastSeg ? `${titleCase(decodeURIComponent(lastSeg))} · ${brand}` : brand,
    description: `Open this link on ${domain}`,
  };
};

export const initialsOf = (name = '') =>
  name
    .split(' ')
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';

export const fmtTime = (d) =>
  new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' });

export const fmtDay = (d) => {
  const date = new Date(d);
  // Compare calendar days in IST (the app's convention), not the host TZ, so the
  // "Today"/"Yesterday" boundary flips at IST midnight.
  const istDayKey = (x) => x.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }); // YYYY-MM-DD
  const todayKey = istDayKey(new Date());
  const dateKey = istDayKey(date);
  if (dateKey === todayKey) return 'Today';
  const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000);
  if (dateKey === istDayKey(yesterday)) return 'Yesterday';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' });
};

export const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

// Whether the free-form (non-template) reply window is still open. Meta allows
// free-form replies only within 24h of the customer's last inbound message.
export const isWindowOpen = (lastInboundAt) => {
  if (!lastInboundAt) return false;
  const now = Date.now();
  return now - new Date(lastInboundAt).getTime() < 24 * 60 * 60 * 1000;
};

export const windowHoursLeft = (lastInboundAt) => {
  if (!lastInboundAt) return 0;
  const now = Date.now();
  const left = 24 * 60 * 60 * 1000 - (now - new Date(lastInboundAt).getTime());
  return Math.max(0, Math.floor(left / (60 * 60 * 1000)));
};

// Human label for the time left in the 24h window. Under an hour, show minutes
// (e.g. "45m left") instead of flooring to a misleading "0h left".
export const windowTimeLeftLabel = (lastInboundAt) => {
  if (!lastInboundAt) return '';
  const left = 24 * 60 * 60 * 1000 - (Date.now() - new Date(lastInboundAt).getTime());
  if (left <= 0) return '';
  const hrs = Math.floor(left / (60 * 60 * 1000));
  if (hrs >= 1) return `${hrs}h left`;
  const mins = Math.max(1, Math.floor(left / (60 * 1000)));
  return `${mins}m left`;
};

// --- demo seed data --------------------------------------------------------

export const SEED_TAGS = [];

export const SEED_CONTACTS = [];

export const SEED_CONVERSATIONS = [];

export const SEED_TEMPLATES = [];

export const SEED_BROADCASTS = [];

export const SEED_QUICK_REPLIES = [
  "Hello! We are absolutely thrilled that you are here. 🌟 Reseller Bazzar is dedicated to helping independent sellers, manufacturers, and wholesalers reach thousands of buyers. Let’s bring your business online and grow together! Click this link to complete your quick and easy registration: https://www.resellerbazzar.in/information/how-to-sign-up. We are here to support you every single step of the way. 💼❤️",
  "Hello! Ready to showcase your amazing products to the world? 🚀 Creating a beautiful catalog is the first step to success. Follow our simple, step-by-step guide to list your products and post ads: https://www.resellerbazzar.in/information/how-to-post-ads. If you need any personal assistance, just let us know. Let's make your products shine! ✨📦",
  "Hello! We believe in your business's potential to achieve great heights. 📈 To help you get maximum visibility, inquiries, and orders, we've designed custom premium plans tailored for your growth. Explore our plans here: https://www.resellerbazzar.in/plans. Your success is our true reward, and we're excited to partner with you! 🌟💼",
  "Welcome to Reseller Bazzar! We are so glad you are here. 🤝 Find verified wholesalers, manufacturers, and unique products from across India. Start searching our wide catalog to connect with great sellers: https://www.resellerbazzar.in/all-products. Let us know if you need help finding a specific item. Happy sourcing! 🛍️✨",
  "Hello! Sourcing in bulk or have a custom requirement? 📋 Save time and money by posting a Request for Quote (RFQ). Verified suppliers who match your requirements will contact you directly with their best offers: https://www.resellerbazzar.in/rfq. Let's find exactly what you need at the best price! 🤝🌟",
  "Hello! Your safety, peace of mind, and trust are everything to us at Reseller Bazzar. 🛡️❤️ To ensure you have a secure, pleasant, and successful trading experience, please take a quick look at our safety tips here: https://www.resellerbazzar.in/information/buyer-safety-guide. We've got your back! Safe trading! 🤝"
];

export const SEED_KEYWORD_RULES = [
  {
    id: "seed-r1",
    keywords: ["join", "register", "signup", "sell", "seller", "start business"],
    reply: "We are so excited to welcome you to the Reseller Bazzar family! 🌟 Whether you are a manufacturer, wholesaler, or reseller, we are here to empower your business. Click below to register and start selling online today! 🚀💼",
    buttons: [
      { type: "url", title: "Register Now 🚀", url: "https://www.resellerbazzar.in/signup" },
      { type: "url", title: "Registration Guide 📖", url: "https://www.resellerbazzar.in/information/how-to-sign-up" }
    ]
  },
  {
    id: "seed-r2",
    keywords: ["upload", "post", "list", "ad", "ads", "product", "add product"],
    reply: "Showcase your products to buyers across India! 📦 Listing your items is simple, quick, and highly effective. Let's get your products online and start attracting customers right away. ✨",
    buttons: [
      { type: "url", title: "Add Your Business 🏢", url: "https://www.resellerbazzar.in/information/how-to-add-your-business-online" },
      { type: "url", title: "How to Post Ads 📝", url: "https://www.resellerbazzar.in/information/how-to-post-ads" }
    ]
  },
  {
    id: "seed-r3",
    keywords: ["plan", "price", "cost", "upgrade", "premium", "membership"],
    reply: "Let's accelerate your business growth! 📈 Explore our premium membership plans designed to give your products maximum visibility and get direct buyer leads. Your success is our mission. 💼✨",
    buttons: [
      { type: "url", title: "View Pricing Plans 💳", url: "https://www.resellerbazzar.in/plans" }
    ]
  },
  {
    id: "seed-r4",
    keywords: ["buy", "search", "find", "shop", "catalog", "buyer"],
    reply: "Looking to buy products? We connect you directly with top wholesalers and manufacturers in India. 🛍️ Let us help you find the best quality products at wholesale prices! ✨",
    buttons: [
      { type: "url", title: "Browse Catalog 🛍️", url: "https://www.resellerbazzar.in/all-products" },
      { type: "url", title: "Explore Businesses 🏢", url: "https://www.resellerbazzar.in/all-businesses" }
    ]
  },
  {
    id: "seed-r5",
    keywords: ["rfq", "quote", "bulk", "requirement", "request", "custom requirement"],
    reply: "Need to source in bulk or have a specific requirement? 📋 Post a Request for Quote (RFQ) and receive customized deals and quotes directly from verified suppliers. Save time and get the best prices! 🤝",
    buttons: [
      { type: "url", title: "Post RFQ Now 📋", url: "https://www.resellerbazzar.in/rfq" }
    ]
  },
  {
    id: "seed-r6",
    keywords: ["safety", "scam", "fraud", "trust", "secure", "verify"],
    reply: "Your safety and trust are our absolute priorities. 🛡️ We want you to trade with total confidence on Reseller Bazzar. Please read our safety guidelines to stay protected against scams. ❤️",
    buttons: [
      { type: "url", title: "Buyer Safety Guide 🛡️", url: "https://www.resellerbazzar.in/information/buyer-safety-guide" },
      { type: "url", title: "Scam Alerts ⚠️", url: "https://www.resellerbazzar.in/information/security-scam-alert" }
    ]
  }
];

